import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { ReviewEntities } from 'client/data/models/dealer-reviews';
import { TIME } from 'client/utils/time';
import { createDate } from 'site-modules/shared/utils/date-utils';
import { RatingStars } from 'site-modules/shared/components/rating-stars/rating-stars';
import { Link } from 'site-modules/shared/components/link/link';

import './user-dealer-review.scss';

function ReviewLineItem({ flag = null, text }) {
  return flag === null ? null : (
    <li className="small mb-0">
      {' '}
      {text}
      <span className="user-review-is-recommended">
        <strong> {flag} </strong>
      </span>
    </li>
  );
}

ReviewLineItem.propTypes = {
  flag: PropTypes.string,
  text: PropTypes.string.isRequired,
};

export function UserDealerReview({ review, isUpdate = false, updateLink = undefined }) {
  const { createdIso, updatedIso, updated } = review;
  const showUpdatedString = !!updatedIso && createDate(updatedIso) - createDate(createdIso) > TIME.MS.FIVE_DAYS;

  return (
    <div className="dealership-user-review text-gray-darker">
      <div>
        <RatingStars className="user-review-rating text-success pe-0_5" rating={review.rating} />
        <span className="xsmall text-capitalize">{review.reviewType} Rating</span>
      </div>
      <div>
        <div className="mb-0_5">
          <span className="user-review-title">
            <strong>{review.title}</strong>
          </span>
        </div>
      </div>
      <div className="small mb-1">
        Written by
        <span className="user-review-author">
          <strong> {review.author} </strong>
        </span>
        on
        <span className="user-review-creation-date"> {review.created}</span>
        {showUpdatedString && (
          <div className="d-inline-block px-0_25 ms-0_25 text-white bg-primary-darker rounded">updated {updated}</div>
        )}
        {review.hasPreviousDealer && <strong> - This review was published during previous ownership</strong>}
      </div>
      <p className="user-review-text mb-0 size-16">{review.text}</p>
      <div className={classNames('review-details')}>
        <div className="mb-1 mt-1_5">
          <ul className="list-unstyled mb-0_5">
            <ReviewLineItem flag={review.isDealerRecommended} text="Recommend this dealer?" />
            <ReviewLineItem flag={review.wasCarBoughtHere} text="Purchased a vehicle from this dealer?" />
            <ReviewLineItem
              flag={review.wereYouSatisfied}
              text="Were you satisfied with the quality of work performed on your vehicle?"
            />
            <ReviewLineItem
              flag={review.didDealerCompleteWork}
              text="Did the dealership complete the work within the timeframe promised?"
            />
            <ReviewLineItem
              flag={review.didDealerProvideQuote}
              text="Did the dealership provide you with an accurate quote of the work to be performed?"
            />
            <ReviewLineItem
              flag={review.wasPriceAppropriate}
              text="Do you feel the price paid for service(s) was fair and appropriate?"
            />
            <ReviewLineItem
              flag={review.didDealerAllCommitmentsMade}
              text="Did the dealer honor all commitments made?"
            />
          </ul>
        </div>
        {!isUpdate && (
          <div>
            <a
              className="text-primary-darker small text-underline"
              href={`https://help.edmunds.com/hc/en-us/requests/new?ticket_form_id=2343&ticket[subject]=Report%20review%20(${
                review.id
              })%20to%20Edmunds.com%20for%20investigation&ticket[description]=I%20am%20reporting%20this%20review%20%22${
                review.title
              }%22%20posted%20by%20${review.author}%20because...`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Report
            </a>
            {!!updateLink && (
              <>
                <span className="small mx-0_75">|</span>
                <Link className="text-primary-darker small text-underline" to={updateLink} rel="nofollow">
                  Update review
                </Link>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

UserDealerReview.propTypes = {
  review: ReviewEntities.ReviewEntity.isRequired,
  isUpdate: PropTypes.bool,
  updateLink: PropTypes.string,
};
